import React, { useState, useEffect, useRef } from 'react';
import { setToken } from '../../services/api';
import RecuperarPasswordModal from './RecuperarPasswordModal';
import '../../styles/Auth.css';

function Login({ onLoginSuccess }) {
  const [credentials, setCredentials] = useState({ usuario: '', contrasena: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRecuperarPassword, setShowRecuperarPassword] = useState(false);

  // Estado para bloqueo temporal con conteo regresivo
  const [bloqueado, setBloqueado] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(0);

  // Estado para colegio detectado por subdominio
  const [colegioDetectado, setColegioDetectado] = useState(null);
  const [esLoginSistema, setEsLoginSistema] = useState(false);
  const [cargando, setCargando] = useState(true);

  // Detectar tipo de login síncronamente para no bloquear el render
  const hostname = window.location.hostname;
  const matchSubdominioSync = hostname.match(/^([^.]+)\.uacademik\.codecolombia\.com$/);
  const esSubdominio = !!(matchSubdominioSync && matchSubdominioSync[1]);

  const inputUsuarioRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  // Efecto para el conteo regresivo
  useEffect(() => {
    if (bloqueado && segundosRestantes > 0) {
      const timer = setInterval(() => {
        setSegundosRestantes(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setBloqueado(false);
            setError('');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [bloqueado, segundosRestantes]);

  // Detectar tipo de login segun hostname
  useEffect(() => {
    const detectarTipoLogin = async () => {
      const hostname = window.location.hostname;

      // Patron: subdominio.uacademik.codecolombia.com
      const matchSubdominio = hostname.match(/^([^.]+)\.uacademik\.codecolombia\.com$/);

      // Si es localhost o el dominio principal sin subdominio -> Login de Sistema
      if (hostname === 'localhost' ||
          hostname === 'uacademik.codecolombia.com' ||
          hostname === 'www.uacademik.codecolombia.com' ||
          !matchSubdominio) {
        setEsLoginSistema(true);
        document.title = 'Administración | UAcademiK';
        setCargando(false);
        return;
      }

      // Si hay subdominio, buscar el colegio
      if (matchSubdominio && matchSubdominio[1]) {
        const subdominio = matchSubdominio[1];

        try {
          const res = await fetch(`${apiUrl}/colegios/por-subdominio/${subdominio}`);
          if (res.ok) {
            const data = await res.json();
            setColegioDetectado(data);
            document.title = `Iniciar Sesión | ${data.nombre}`;
          } else {
            setError('Universidad no encontrada para este subdominio');
          }
        } catch (err) {
          console.error('Error cargando universidad por subdominio:', err);
          setError('Error al conectar con el servidor');
        }
      }

      setCargando(false);
    };

    detectarTipoLogin();
  }, [apiUrl]);

  // Focus en input de usuario cuando termina de cargar
  useEffect(() => {
    if (!cargando && inputUsuarioRef.current) {
      inputUsuarioRef.current.focus();
    }
  }, [cargando, colegioDetectado, esLoginSistema]);

  const handleInputChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginData = {
        usuario: credentials.usuario,
        contrasena: credentials.contrasena
      };

      // Si es login de colegio, agregar el esquema
      if (!esLoginSistema && colegioDetectado) {
        loginData.esquema = colegioDetectado.nombre_esquema;
      }

      const res = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Guardar token JWT para autenticación en peticiones futuras
        if (data.token) {
          setToken(data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        onLoginSuccess(data.user);
      } else {
        // Si está bloqueado, iniciar conteo regresivo
        if (data?.bloqueado && data?.segundosRestantes) {
          setBloqueado(true);
          setSegundosRestantes(data.segundosRestantes);
          setError(data.message);
        } else {
          setError(data?.message || 'Error al conectar con el servidor');
        }
      }
    } catch (err) {
      setError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // Formatear segundos a MM:SS
  const formatTiempo = (segundos) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Mientras detecta tipo de login en dominio principal, mostrar fondo vacío brevemente
  // Para subdominios de colegio, no bloqueamos: mostramos el formulario de inmediato
  if (cargando && !esSubdominio) {
    return <div className="auth-wrapper"></div>;
  }

  // Login de Sistema (dominio principal) - Estilo administrativo
  if (esLoginSistema) {
    return (
      <div className="auth-wrapper auth-wrapper-sistema">
        <div className="auth-inner auth-inner-sistema">
          <form onSubmit={handleSubmit} autoComplete="off">
            {/* Logo/Icono de sistema */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 12px',
              background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '30px',
              boxShadow: '0 4px 12px rgba(30, 41, 59, 0.3)'
            }}>
              🛡️
            </div>

            <h3 style={{ color: '#1e293b', marginBottom: '8px' }}>UAcademiK</h3>
            <p style={{
              color: '#64748b',
              fontSize: '14px',
              margin: '0 0 24px 0',
              textAlign: 'center'
            }}>
              ⚙️ Panel de Administración
            </p>

            <div className="form-group">
              <label>Usuario</label>
              <input
                ref={inputUsuarioRef}
                name="usuario"
                type="text"
                className="form-control"
                onChange={handleInputChange}
                value={credentials.usuario}
                required
                autoFocus
                autoComplete="nope"
                placeholder="Ingrese su usuario"
              />
            </div>

            <div className="form-group">
              <label>Contraseña</label>
              <input
                name="contrasena"
                type="password"
                className="form-control"
                onChange={handleInputChange}
                value={credentials.contrasena}
                required
                autoComplete="new-password"
                placeholder="Ingrese su contraseña"
              />
            </div>

            {error && !bloqueado && <div className="auth-error">{error}</div>}

            {bloqueado && (
              <div className="auth-bloqueo">
                <div className="bloqueo-icon">🔒</div>
                <div className="bloqueo-mensaje">{error}</div>
                <div className="bloqueo-contador">{formatTiempo(segundosRestantes)}</div>
                <div className="bloqueo-texto">para volver a intentar</div>
              </div>
            )}

            <button
              type="submit"
              className="btn-auth"
              style={{
                background: bloqueado
                  ? '#94a3b8'
                  : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
              }}
              disabled={loading || bloqueado}
            >
              {loading ? 'Ingresando...' : bloqueado ? 'Bloqueado' : 'Ingresar'}
            </button>

            <p className="forgot-password-link">
              <button
                type="button"
                onClick={() => setShowRecuperarPassword(true)}
                className="link-button"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </p>
          </form>

          <RecuperarPasswordModal
            isOpen={showRecuperarPassword}
            onClose={() => setShowRecuperarPassword(false)}
            esquema={null}
          />
        </div>
      </div>
    );
  }

  // Login de Colegio (subdominio detectado o en proceso de detección)
  if (colegioDetectado || esSubdominio) {
    return (
      <div className="auth-wrapper auth-wrapper-colegio">
        <div className="auth-inner auth-inner-colegio">
          <form onSubmit={handleSubmit} autoComplete="off">
            {/* Logo/Icono de colegio */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '30px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}>
              🏛️
            </div>

            <h3 style={{ color: '#1e40af', fontSize: '22px' }}>
              {colegioDetectado ? colegioDetectado.nombre : 'Cargando...'}
            </h3>
            <p className="login-subtitle">Sistema de Gestión Universitaria</p>

            {/* Indicador de año académico si está disponible */}
            {colegioDetectado?.anio && (
              <div style={{
                background: '#dbeafe',
                color: '#1e40af',
                padding: '6px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                marginBottom: '20px',
                display: 'inline-block'
              }}>
                📅 Período Académico {colegioDetectado.anio}
              </div>
            )}

            <div className="form-group">
              <label>Usuario</label>
              <input
                ref={inputUsuarioRef}
                name="usuario"
                type="text"
                className="form-control"
                onChange={handleInputChange}
                value={credentials.usuario}
                required
                autoFocus
                autoComplete="nope"
                placeholder="Número de documento"
              />
            </div>

            <div className="form-group">
              <label>Contraseña</label>
              <input
                name="contrasena"
                type="password"
                className="form-control"
                onChange={handleInputChange}
                value={credentials.contrasena}
                required
                autoComplete="new-password"
                placeholder="Ingrese su contraseña"
              />
            </div>

            {error && !bloqueado && <div className="auth-error">{error}</div>}

            {bloqueado && (
              <div className="auth-bloqueo">
                <div className="bloqueo-icon">🔒</div>
                <div className="bloqueo-mensaje">{error}</div>
                <div className="bloqueo-contador">{formatTiempo(segundosRestantes)}</div>
                <div className="bloqueo-texto">para volver a intentar</div>
              </div>
            )}

            <button
              type="submit"
              className="btn-auth"
              style={{
                background: (bloqueado || !colegioDetectado)
                  ? '#94a3b8'
                  : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
              }}
              disabled={loading || bloqueado || !colegioDetectado}
            >
              {!colegioDetectado ? 'Conectando...' : loading ? 'Ingresando...' : bloqueado ? 'Bloqueado' : 'Ingresar'}
            </button>

            <p className="forgot-password-link">
              <button
                type="button"
                onClick={() => setShowRecuperarPassword(true)}
                className="link-button"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </p>
          </form>

          {/* Footer con powered by */}
          <div style={{
            marginTop: '20px',
            paddingTop: '15px',
            borderTop: '1px solid #e2e8f0',
            textAlign: 'center',
            fontSize: '11px',
            color: '#94a3b8'
          }}>
            Powered by <span style={{ fontWeight: '600', color: '#64748b' }}>UAcademiK</span>
          </div>

          <RecuperarPasswordModal
            isOpen={showRecuperarPassword}
            onClose={() => setShowRecuperarPassword(false)}
            esquema={colegioDetectado?.nombre_esquema}
          />
        </div>
      </div>
    );
  }

  // Error: no se detecto ni sistema ni colegio
  return (
    <div className="auth-wrapper">
      <div className="auth-inner">
        <h3>UAcademiK</h3>
        <p className="login-subtitle">Sistema de Gestión Universitaria</p>
        <div className="auth-error">
          {error || 'No se pudo determinar la universidad. Verifique la URL.'}
        </div>
      </div>
    </div>
  );
}

export default Login;
